/**
 * Cal.com booking configuration.
 *
 * WHY THIS FILE EXISTS
 *   Until 2026-08-18 this site had no scheduler, and said so in its own copy:
 *   "There's no booking system here, on purpose." That was honest — a form that
 *   posts to /contact cannot hold a time. Aaron created and will operate the
 *   Cal.com account (`frontrangebikes`, ah+frb@aaronhanson.com), so the site
 *   can now offer a real test-ride slot instead of a promise to write back.
 *
 * THE EMBED NEEDS NO API KEY.
 *   Cal.com's embed takes only the public URL. The API key (stored in 1Password
 *   as "Front Range Bikes cal.com", field `api_key`) is for reading or writing
 *   bookings programmatically, which nothing here does. Do not wire a secret
 *   into the frontend — anything shipped to the browser is public.
 *
 * ONE PLACE TO CHANGE.
 *   Every CTA, every embed and every test reads from here. Aaron owns the
 *   account and ongoing configuration; if the event slug changes, this file is
 *   the whole site edit.
 */

/** The Cal.com username. Becomes cal.com/<user>/<slug>. */
export const CAL_USER = "frontrangebikes";

/**
 * The event slug for a test ride.
 *
 * Real event, created through the Cal.com v2 API on 2026-08-18: "Test ride",
 * 30 minutes, location set to the shop's street address (2500 47th St Unit 12)
 * rather than Cal Video — this is somebody coming to ride a bike, not a call.
 * Verified live: cal.com/frontrangebikes/test-ride returns 200.
 *
 * Replaced `30min`, which was Cal.com's auto-created default and titled
 * "30 min meeting" — accurate about the duration and wrong about everything
 * else.
 */
export const TEST_RIDE_SLUG = "test-ride";

/**
 * Legacy event slug for shop maintenance.
 *
 * Kept only for API cleanup/history. The site no longer links or embeds this
 * event: Ryan confirmed on 2026-08-25 that maintenance should start with a
 * phone call or text and proceed first come, first served.
 */
export const MAINTENANCE_SLUG = "maintenance";

/** Public booking URL. Safe to render, safe to link, contains no secret. */
export const bookingUrl = (slug: string = TEST_RIDE_SLUG) =>
  `https://cal.com/${CAL_USER}/${slug}`;

/** The `data-cal-link` value Cal.com's embed script expects. */
export const calLink = (slug: string = TEST_RIDE_SLUG) => `${CAL_USER}/${slug}`;

/**
 * Master switch.
 *
 * ON as of 2026-08-18: the real "test-ride" event exists and the copy in
 * lib/copy/contact.ts was rewritten in the same commit.
 *
 * The two must move together — a page that offers a calendar must not also say
 * there is no booking system. `script/booking_check.mjs` reads the rendered
 * page and fails if they ever disagree, so this cannot drift silently.
 */
export const BOOKING_ENABLED = true;
